import { Key, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSwipeable } from 'react-swipeable';
import trackTabChange from '@helpers/analyticsHelpers/trackTabChangeProductDetails';
import TabContent from '@molecules/Tabs/TabContent';
import ProductTableOfContents from '@molecules/ProductTableOfContents/ProductTableOfContents';
import ProductTableOfOther from '@molecules/ProductTableOfOther/ProductTableOfOther';
import ExpandableContainer from '@molecules/ExpandableContainer/ExpandableContainer';
import ProductTableOfInformation from '@molecules/ProductTableOfInformation/ProductTableOfInformation';
import { useSearchParams } from 'next/navigation';
import { StyledSwipeableSection, StyledSwipeableSectionTabs } from './ProductDetailsTabs.styles';
import useResponsive from '@hooks/useResponsive';

type Props = {
  data: any;
};

const ProductDetailsTabs = ({ data }: Props) => {
  const searchParams = useSearchParams();
  const queryTab = searchParams?.get('tab');
  const t = useTranslations('product');
  const [activeTab, setActiveTab] = useState('1');
  const [initiallyExpanded, setInitiallyExpanded] = useState(false);
  const { isMobile } = useResponsive();

  const onTabChange = (key: Key) => {
    trackTabChange(key);
    setActiveTab(String(key));
  };

  const swipeHandlers = useSwipeable({
    onSwiped: (e) => {
      if (isMobile) {
        if (e.dir === 'Left') {
          if (+activeTab + 1 > 3) return;
          setActiveTab((+activeTab + 1).toString());
        }
        if (e.dir === 'Right') {
          if (+activeTab - 1 < 1) return;
          setActiveTab((+activeTab - 1).toString());
        }
      }
    },
  });

  useEffect(() => {
    if (queryTab && ['1', '2', '3'].includes(queryTab)) {
      setActiveTab(queryTab);
      setInitiallyExpanded(true);
    } else {
      setActiveTab('1');
    }
  }, [data, queryTab]);

  return (
    <StyledSwipeableSection {...swipeHandlers}>
      <StyledSwipeableSectionTabs
        variant="product"
        activeTab={activeTab}
        onTabChange={onTabChange}
        slideIndex={isMobile ? +activeTab : 0}
      >
        <TabContent key="1" title={t('tabs.product_information')}>
          <ExpandableContainer
            key="1"
            initiallyExpanded={data.medicineType !== '' || initiallyExpanded}
            meta="product_information"
          >
            <ProductTableOfInformation data={data} />
          </ExpandableContainer>
        </TabContent>

        {(data?.ingredients || data?.animalData || data?.nutrientComponents.length > 0) && (
          <TabContent key="2" title={t('tabs.table_of_contents')}>
            <ExpandableContainer key="2" meta="table_of_contents" initiallyExpanded={initiallyExpanded}>
              <ProductTableOfContents product={data} />
            </ExpandableContainer>
          </TabContent>
        )}

        <TabContent key="3" title={t('tabs.other')}>
          <ExpandableContainer key="3" initiallyExpanded={initiallyExpanded} meta="other">
            <ProductTableOfOther data={data} />
          </ExpandableContainer>
        </TabContent>
      </StyledSwipeableSectionTabs>
    </StyledSwipeableSection>
  );
};

export default ProductDetailsTabs;
